/**
 * What a composed run looks like to the person waiting on it: every stage's
 * label, and the making-of film materials derived from the run's durable
 * state. ONE source for the page's progress lines, the provisional film the
 * functions write at every stage boundary, and the builder's beat after every
 * provider call inside a stage. A stage kind added to the run must add its
 * label and its film beats here (the switches are exhaustive), so no server
 * stage can run without the page showing it.
 *
 * Every text is player-facing: no internal module ids, no validator output.
 */
import { MODULE_ID_PATTERN, type ComposeModuleNeedV1 } from './composePlan.js';
import type { CreationFilmMaterialV1 } from './creationFilms.js';
import type { GameDesign } from './design/types.js';
import { composedPlannedStepsV1, type ComposedPlannedStepV1, type ComposedRevisionBaseV1 } from './spec/composedSelection.js';
import type { ComposedRunStageV1, ComposedRunStagesV1 } from './types.js';

/** Any module id in prose (the module-id grammar, optionally dotted further:
 * `structure.verbProofs`), with a namespace of two or more letters so
 * abbreviations such as "e.g." stay prose. */
const INTERNAL_NAME = new RegExp(`\\b(?=[a-z]{2,}\\.)${MODULE_ID_PATTERN}(?:\\.\\w+)*\\b`, 'g');

/** Model-written plan text can quote internal names such as rule.contactDamage
 * or flow.escalation. Say the last part in words: "contact damage", "escalation". */
export function plainPlanText(text: string): string {
  return text.replace(INTERNAL_NAME, name => name.slice(name.lastIndexOf('.') + 1)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase());
}

/** The progress lines of the stages every composed run shares, in the creator's terms. */
export const COMPOSED_STAGE_LABELS = {
  plan: 'Planning how your game is built',
  synthesis: 'Fitting your idea onto the game and proving it is winnable',
  build: 'Starting the build',
} as const;

/** The first step of a revision: the live game it changes. */
const COMPOSED_BASE_STEP_LABEL = 'Starting from your current game';

/** A new rule's name in words: rule.snakeMove → "snake move". */
export function composedRuleNameV1(need: Pick<ComposeModuleNeedV1, 'id'>): string {
  return plainPlanText(need.id);
}

/** A step's progress line, in the creator's terms. */
export function composedCreateStepLabelV1(step: ComposedPlannedStepV1): string {
  switch (step.kind) {
    case 'base': return COMPOSED_BASE_STEP_LABEL;
    case 'template': return 'Composing a new game from the platform’s proven parts';
    case 'adaptation': return 'Adapting the template to your idea';
    case 'rule': return `Writing a new part: ${composedRuleNameV1(step.need)}`;
  }
}

/** Any stage's progress line: the plan, a planned step, synthesis, or a revision's build. */
export function composedRunStageLabelV1(stage: ComposedRunStageV1 | 'build', planned: readonly ComposedPlannedStepV1[]): string {
  if (stage === 'plan' || stage === 'synthesis' || stage === 'build') return COMPOSED_STAGE_LABELS[stage];
  const step = planned[Number(stage.slice(5))];
  return step ? composedCreateStepLabelV1(step) : 'Building the next part of your game';
}

/** The run fields the film is derived from (a Create or a revision run). */
export type ComposedFilmRunV1 = Pick<ComposedRunStagesV1, 'plan' | 'steps' | 'result' | 'attempt' | 'temporaryRetry' | 'recovery' | 'createdAt'> & {
  stage: ComposedRunStageV1 | 'build';
  status: string;
  /** A Create that restarted on the closest stock game. */
  fallback?: { at: number };
};

/** The planned steps of a saved run. A revision on its base route receipts
 * the live game as step 0, so every later stage reads the base from there. */
export function composedRunPlannedStepsV1(run: Pick<ComposedFilmRunV1, 'plan' | 'steps'>,
  base: ComposedRevisionBaseV1 | null): ComposedPlannedStepV1[] {
  if (!run.plan) return [];
  const first = run.steps[0];
  return composedPlannedStepsV1(run.plan, base ?? (first?.kind === 'base' ? first.base : null));
}

export interface ComposedRunFilmInputV1 {
  runId: string;
  creationId: string;
  source: Extract<CreationFilmMaterialV1['source'], 'analysis' | 'revision'>;
  run: ComposedFilmRunV1;
  planned: readonly ComposedPlannedStepV1[];
  design: GameDesign;
  /** When this sync happens: the time a newly seen beat is stamped with. */
  at: number;
}

const TITLE_MAX = 200;
const TEXT_MAX = 1_500;
const LIST_MAX = 6;
const CAST_MAX = 4;

const clip = (text: string, max: number): string => {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length <= max ? flat : `${flat.slice(0, max - 1).trimEnd()}…`;
};
const list = (items: readonly string[]): string => items.slice(0, LIST_MAX).map(item => `• ${plainPlanText(item)}`).join('\n');

/** The finished step's card. */
function stepDoneCard(step: ComposedPlannedStepV1): { title: string; text: string } {
  switch (step.kind) {
    case 'base': return { title: 'Starting from your current game', text: 'Everything you already made stays as it is.' };
    case 'template': return { title: 'Parts we’re reusing', text: list(step.included) || 'Proven parts from the platform library.' };
    case 'adaptation': return { title: 'Shaped to your idea', text: list(step.changes) || 'The template now fits your idea.' };
    case 'rule': return { title: `A new part: ${composedRuleNameV1(step.need)}`, text: plainPlanText(step.need.need) };
  }
}

/** What the running stage is doing, in one sentence. */
function runningLine(stage: ComposedFilmRunV1['stage'], planned: readonly ComposedPlannedStepV1[]): string {
  if (stage === 'plan') return 'Deej is working out which parts your game needs.';
  if (stage === 'synthesis') return 'Deej is fitting everything together and playing it to prove it can be won.';
  if (stage === 'build') return 'Deej is handing your game to the builder.';
  const step = planned[Number(stage.slice(5))];
  switch (step?.kind) {
    case 'rule': return `Deej is writing and testing a new part: ${composedRuleNameV1(step.need)}.`;
    case 'template': return 'Deej is putting your game together from proven parts.';
    case 'adaptation': return 'Deej is reshaping a proven game to fit your idea.';
    case 'base': return 'Deej is starting from your current game.';
    case undefined: return 'Deej is building the next part of your game.';
  }
}

/** The design cards: how it plays, the goal, the controls, the look, the cast. */
function designCards(design: GameDesign): Array<{ id: string; title: string; text: string }> {
  const cards = [
    { id: 'design-play', title: 'How you play', text: design.summary },
    { id: 'design-goal', title: 'Your challenge', text: design.winCondition },
    { id: 'design-controls', title: 'Controls', text: design.controls },
    { id: 'design-theme', title: 'The look', text: design.theme ?? '' },
    ...design.entities.slice(0, CAST_MAX).map((entity, index) => ({
      id: `design-cast-${index + 1}`, title: `Meet the ${entity.name}`, text: entity.role,
    })),
  ];
  return cards.filter(card => card.text.trim());
}

/**
 * Every film material the run's current durable state implies. The caller
 * stores only ids it has not stored yet, so a beat keeps the time it was first
 * seen and the list only grows as the run advances: design cards, then a
 * "now" beat per claim of each stage, a beat per outage wait or rule fix, a
 * card per finished stage, and the restart on a stock game.
 */
export function composedRunFilmMaterialsV1(input: ComposedRunFilmInputV1): CreationFilmMaterialV1[] {
  const { run, planned } = input;
  const material = (materialId: string, kind: 'concept' | 'other', title: string, text: string, createdAt: number): CreationFilmMaterialV1 => ({
    schemaVersion: 1, runId: input.runId, creationId: input.creationId, materialId, kind, source: input.source,
    title: clip(title, TITLE_MAX), text: clip(text, TEXT_MAX), createdAt,
  });
  const out = designCards(input.design).map(card => material(card.id, 'concept', card.title, card.text, run.createdAt));
  if (run.fallback) {
    out.push(material('stage-restart-stock', 'other', 'Starting over from a proven game',
      'Your idea was hard to finish as planned, so Deej is building it on the closest proven game instead.', input.at));
  }
  if (run.plan) out.push(material('stage-plan-done', 'other', 'Your game plan', list(run.plan.included) || 'Deej has a plan.', input.at));
  run.steps.forEach((_step, index) => {
    const step = planned[index];
    if (!step) return;
    const card = stepDoneCard(step);
    out.push(material(`stage-step-${index}-done`, 'other', card.title, card.text, input.at));
  });
  if (run.result) {
    out.push(material('stage-synthesis-done', 'other', 'Put together and play-tested',
      'Deej fitted your idea onto the game and played it to prove it can be won.', input.at));
  }
  if (run.temporaryRetry) {
    out.push(material(`stage-${run.stage}-wait-${run.temporaryRetry.count}`, 'other', 'Taking a short break',
      'A service Deej relies on is busy. Deej will try this step again in a moment.', input.at));
  }
  if (run.recovery?.stage === run.stage) {
    out.push(material(`stage-${run.stage}-fix-${run.recovery.automaticRetries}`, 'other', 'Fixing a new part',
      'The last try didn’t pass its tests. Deej is writing it again with what it learned.', input.at));
  }
  if (run.status === 'claimed') {
    out.push(material(`stage-${run.stage}-try-${run.attempt}`, 'other', `Now: ${composedRunStageLabelV1(run.stage, planned)}`,
      runningLine(run.stage, planned), input.at));
  }
  return out;
}

/** The builder's beat after one provider call inside a claimed stage: the
 * call is written and is now being checked. `call` counts within the claim. */
export function composedRunCallBeatV1(input: Omit<ComposedRunFilmInputV1, 'design'> & { call: number }): CreationFilmMaterialV1 {
  const label = composedRunStageLabelV1(input.run.stage, input.planned);
  return {
    schemaVersion: 1, runId: input.runId, creationId: input.creationId,
    materialId: `stage-${input.run.stage}-try-${input.run.attempt}-call-${input.call}`, kind: 'other', source: input.source,
    title: clip(`Testing try ${input.call}: ${label}`, TITLE_MAX),
    text: input.call === 1
      ? 'Deej finished a first try and is testing it now.'
      : `Try ${input.call - 1} didn’t pass every test, so Deej wrote try ${input.call} and is testing it now.`,
    createdAt: input.at,
  };
}
